import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useAffiliateTracking } from '@/hooks/useAffiliateTracking';
import { Button } from '@/components/ui/button';
import { RetreatsHero } from '@/components/retreats/RetreatsHero';
import { NextRetreatModule } from '@/components/retreats/NextRetreatModule';
import { RetreatLegacy } from '@/components/retreats/RetreatLegacy';
import { RetreatJourneyByYear } from '@/components/retreats/RetreatJourneyByYear';
import { CostaRicaFeature } from '@/components/retreats/CostaRicaFeature';
import { JustBePillars } from '@/components/retreats/JustBePillars';
import { HeartVideoSection } from '@/components/retreats/HeartVideoSection';
import { PhotoWall } from '@/components/retreats/PhotoWall';
import { TransformationStories } from '@/components/retreats/TransformationStories';
import { MeetHostsSection } from '@/components/retreats/MeetHostsSection';
import { RetreatsFAQ } from '@/components/retreats/RetreatsFAQ';
import { PriorityListSection } from '@/components/retreats/PriorityListSection';

const scrollToPriorityList = () => {
  document.getElementById('priority-list')?.scrollIntoView({ behavior: 'smooth' });
};

const NAV_LINKS = [
  { href: '#next-retreat', label: 'Next Retreat' },
  { href: '#our-story', label: 'Our Story' },
  { href: '#journey', label: 'Timeline' },
  { href: '#experience', label: 'Experience' },
  { href: '#stories', label: 'Stories' },
  { href: '#faq', label: 'FAQ' },
];

export default function RetreatsHubPage() {
  const { user } = useAuth();
  useAffiliateTracking();

  useEffect(() => {
    document.title = 'Sister Diamonds Retreats — Just Be™ Retreat Experiences';
    if (window.location.hash) {
      setTimeout(() => {
        document.querySelector(window.location.hash)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="min-h-screen bg-cream" data-testid="retreats-hub-page">
      <RetreatsHero onPriorityList={scrollToPriorityList} />

      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-sand shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="font-heading text-xl font-bold text-forest">
              Sister Diamonds <span className="hidden sm:inline text-terracotta text-sm font-medium">Retreats</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              {NAV_LINKS.map((l) => (
                <a key={l.href} href={l.href} className="text-gray-600 hover:text-forest transition text-sm">{l.label}</a>
              ))}
            </div>
            {user ? (
              <Link to="/dashboard">
                <Button variant="outline" size="sm" className="btn-outline">Dashboard</Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button size="sm" className="bg-forest text-white hover:bg-forest/90">Sign In</Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      <NextRetreatModule onPriorityList={scrollToPriorityList} />
      <RetreatLegacy />
      <RetreatJourneyByYear />
      <CostaRicaFeature />
      <JustBePillars />
      <HeartVideoSection />
      <PhotoWall />
      <TransformationStories />
      <MeetHostsSection />
      <RetreatsFAQ />
      <PriorityListSection />

      <footer className="py-8 px-6 bg-forest/95 text-white/60 text-center text-sm">
        <p>©2026 Sister Diamonds, LLC. All Rights Reserved.</p>
        <p className="mt-2">
          Questions? Contact us at <a href="mailto:info@sisterdiamonds.com" className="text-terracotta hover:underline">info@sisterdiamonds.com</a>
        </p>
      </footer>
    </div>
  );
}
